import { useState, useEffect, useCallback, useRef } from 'react';
import type { TimelineEvent } from 'types/timeline';
import { useApiClients } from '../context/ApiClientsContext';

/**
 * Event types relevant to the status tool panel.
 * Each maps to a filter chip in Zone 3.
 */
/**
 * Keyed on `LedgerEntryType` (EAINT-764 Stage 2 / OQ-3), like every other timeline surface.
 *
 * Urgency and gravity no longer have entry types of their own — Stage 3 made them EDGE-SUBJECT
 * ASPECTS, so both arrive as `aspect_observation`. The status panel therefore has one aspect chip
 * rather than separate urgency/gravity chips; distinguishing them would mean filtering on
 * `aspectSystemName`, which this panel's API does not expose.
 */
export type StatusTimelineEventType =
  | 'status_propagated'
  | 'check_in'
  | 'velocity_changed'
  | 'aspect_observation';

export const ALL_STATUS_EVENT_TYPES: StatusTimelineEventType[] = [
  'status_propagated',
  'check_in',
  'velocity_changed',
  'aspect_observation',
];

const STORAGE_KEY_PREFIX = 'status-timeline-filters-';
const DEFAULT_PAGE_SIZE = 20;

export interface UseStatusTimelineOptions {
  sphereId: string | undefined;
  enabledEventTypes: StatusTimelineEventType[];
  pageSize?: number;
}

export interface UseStatusTimelineResult {
  events: TimelineEvent[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  fetchMore: () => void;
  refresh: () => void;
}

/**
 * Load persisted filter state from localStorage for a sphere.
 */
export function loadStatusTimelineFilters(
  sphereId: string
): StatusTimelineEventType[] {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${sphereId}`);
    if (!raw) return [...ALL_STATUS_EVENT_TYPES];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [...ALL_STATUS_EVENT_TYPES];
    }
    // Validate each entry is a known status event type
    const valid = parsed.filter((eventType: string) =>
      (ALL_STATUS_EVENT_TYPES as string[]).includes(eventType)
    ) as StatusTimelineEventType[];
    return valid.length > 0 ? valid : [...ALL_STATUS_EVENT_TYPES];
  } catch {
    return [...ALL_STATUS_EVENT_TYPES];
  }
}

/**
 * Save filter state to localStorage for a sphere.
 */
export function saveStatusTimelineFilters(
  sphereId: string,
  filters: StatusTimelineEventType[]
): void {
  try {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}${sphereId}`,
      JSON.stringify(filters)
    );
  } catch {
    // localStorage full or unavailable — silently fail
  }
}

/**
 * Hook for fetching status-related timeline events with offset-based pagination.
 *
 * Filters by a subset of event types relevant to the status panel (StatusChanged,
 * CheckInSubmitted, VelocityChanged, UrgencyChanged, GravityChanged).
 *
 * IntersectionObserver logic is left to the consuming component — this hook
 * provides data + `fetchMore` / `refresh` callbacks.
 */
export function useStatusTimeline({
  sphereId,
  enabledEventTypes,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseStatusTimelineOptions): UseStatusTimelineResult {
  const apiClients = useApiClients();

  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  // Ref to track the current AbortController for in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);
  // Ref to prevent duplicate concurrent fetches
  const fetchInFlightRef = useRef(false);
  // Serialized filter key to detect changes
  const filterKeyRef = useRef<string>('');
  // Ref to track current events for offset calculation in fetchMore
  const eventsRef = useRef<TimelineEvent[]>([]);

  const fetchEvents = useCallback(
    async (options: { reset: boolean; currentEvents?: TimelineEvent[] }) => {
      if (!sphereId || !apiClients?.graphClient) return;
      if (fetchInFlightRef.current) return;

      fetchInFlightRef.current = true;

      // Cancel any previous in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const { reset, currentEvents = [] } = options;
      const offset = reset ? 0 : currentEvents.length;

      setLoading(true);
      if (reset) {
        setError(null);
      }

      try {
        const params = new URLSearchParams();
        params.set('limit', pageSize.toString());
        params.set('offset', offset.toString());
        params.set('sortOrder', 'desc');

        if (enabledEventTypes.length > 0) {
          params.set('eventTypes', enabledEventTypes.join(','));
        }

        const response = await apiClients.graphClient.get(
          `/spheres/${sphereId}/timeline?${params.toString()}`,
          { signal: controller.signal }
        );

        // Don't update state if this request was aborted
        if (controller.signal.aborted) return;

        if (response.success) {
          let fetchedEvents: TimelineEvent[];
          if (Array.isArray(response.data)) {
            fetchedEvents = response.data as TimelineEvent[];
          } else if (Array.isArray(response.events)) {
            fetchedEvents = response.events as TimelineEvent[];
          } else {
            fetchedEvents = [];
          }

          setEvents((prev) => {
            let updated: TimelineEvent[];
            if (reset) {
              updated = fetchedEvents;
            } else {
              const existingIds = new Set(prev.map((e) => e.id));
              const newEvents = fetchedEvents.filter((e) => !existingIds.has(e.id));
              updated = [...prev, ...newEvents];
            }
            eventsRef.current = updated;
            return updated;
          });

          const moreAvailable =
            response.hasMore ??
            (response.total == null
              ? fetchedEvents.length === pageSize
              : offset + fetchedEvents.length < response.total);
          setHasMore(moreAvailable);
          setError(null);
        } else {
          throw new Error('Timeline fetch returned unsuccessful response');
        }
      } catch (err: unknown) {
        // Ignore abort errors
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (controller.signal.aborted) return;

        console.error('[useStatusTimeline] Failed to fetch timeline:', err);
        setError('statusPanel.error.fetchFailed');
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
        fetchInFlightRef.current = false;
      }
    },
    [sphereId, apiClients?.graphClient, enabledEventTypes, pageSize]
  );

  // Initial fetch + re-fetch when enabledEventTypes or sphereId changes
  useEffect(() => {
    const newFilterKey = `${sphereId}:${[...enabledEventTypes].sort((a, b) => a.localeCompare(b)).join(',')}`;
    const filterChanged = newFilterKey !== filterKeyRef.current;
    filterKeyRef.current = newFilterKey;

    if (!sphereId) {
      setEvents([]);
      eventsRef.current = [];
      setLoading(false);
      setError(null);
      setHasMore(false);
      return;
    }

    // Reset and fetch from offset 0
    if (filterChanged) {
      setEvents([]);
      eventsRef.current = [];
      setHasMore(true);
    }
    fetchEvents({ reset: true });

    return () => {
      // Cancel in-flight request on cleanup
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      fetchInFlightRef.current = false;
    };
  }, [sphereId, enabledEventTypes, fetchEvents]);

  const fetchMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchEvents({ reset: false, currentEvents: eventsRef.current });
    }
  }, [loading, hasMore, fetchEvents]);

  const refresh = useCallback(() => {
    setEvents([]);
    eventsRef.current = [];
    setHasMore(true);
    setError(null);
    fetchEvents({ reset: true });
  }, [fetchEvents]);

  return { events, loading, error, hasMore, fetchMore, refresh };
}
